import type { ImplementedRubricSpec, TypedComparison } from '@/lib/types'
import type { VerifierCriterion } from '@/lib/runs'
import { Icon } from './Icon'

/**
 * One graded rollout, read against the package's published rubric map.
 *
 * The map is the source of truth for which checks exist: the verifier reports only what it
 * managed to evaluate, and a run that fails a structural gate stops early, so the emitted
 * list alone cannot distinguish a check that failed from one that was never reached. Both
 * used to render as a red cross with the same sentence. Declared checks are therefore the
 * rows, and the emitted results are joined onto them.
 *
 * With no map — a legacy task, or one authored outside this platform — the emitted criteria
 * are the rows, exactly as before.
 */

export type CriterionState = 'passed' | 'failed' | 'unscored' | 'skipped' | 'undeclared'

export interface CriterionRow extends TypedComparison {
  id: string
  /** Position in the published rubric map, as numbered on the Rubric tab. Absent without a map,
   *  and for results the map does not declare: there is no rubric entry to point to. */
  number?: number
  label?: string
  state: CriterionState
  feedback?: string
  score?: number
  weight?: number
  component?: string
  required: boolean
}

const MEANING: Record<CriterionState, string> = {
  passed: 'passed',
  failed: 'not passed',
  unscored: 'no result recorded',
  skipped: 'not evaluated: the verifier stopped before reaching this check',
  undeclared: 'reported by the verifier but not declared in the published rubric map',
}

function comparison(result?: VerifierCriterion): TypedComparison {
  // Comparison values always come from this attempt, never a newer package map.
  return { actual: result?.actual, expected: result?.expected, present: result?.present, presence_path: result?.presence_path,
    scored_type: result?.scored_type, state_path: result?.state_path,
    scoring_description: result?.scoring_description }
}

/** Join what the package declares with what this attempt reported. */
export function criterionRows(
  criteria: VerifierCriterion[] | undefined,
  spec?: ImplementedRubricSpec | null,
): CriterionRow[] {
  const emitted = new Map((criteria ?? []).map((c) => [c.id, c]))
  if (!spec?.checks?.length) {
    return (criteria ?? []).map((c) => ({
      ...comparison(c), id: c.id, label: c.label, feedback: c.feedback, score: c.score, weight: c.weight,
      component: c.component, required: c.required_for_pass === true,
      state: c.passed == null ? 'unscored' : c.passed ? 'passed' : 'failed',
    }))
  }
  const rows: CriterionRow[] = spec.checks.map((check, index) => {
    const result = emitted.get(check.id)
    return {
      ...comparison(result), scored_type: result?.scored_type ?? check.scored_type,
      expected: result?.expected ?? check.expected, scoring_description: check.scoring_description,
      id: check.id, number: index + 1, label: check.label, feedback: result?.feedback, score: result?.score,
      // Declared, not reported: the map is what the reviewer was shown, and the
      // submission gate already refused a verifier that contradicts it.
      weight: check.weight, component: check.component, required: check.required_for_pass,
      state: !result ? 'skipped' : result.passed == null ? 'unscored' : result.passed ? 'passed' : 'failed',
    }
  })
  for (const c of criteria ?? []) {
    if (spec.checks.some((check) => check.id === c.id)) continue
    // Never drop a result because the map does not mention it.
    rows.push({
      ...comparison(c), id: c.id, feedback: c.feedback, score: c.score, weight: c.weight,
      component: c.component, required: c.required_for_pass === true, state: 'undeclared',
    })
  }
  return rows
}

export function CriteriaTable({ rows, showScores = true, onOpenCheck }: {
  rows: CriterionRow[]; showScores?: boolean
  /** Opens a check on the page's rubric list; given only when that list shows the map these rows were graded with. */
  onOpenCheck?: (checkId: string) => void
}) {
  if (rows.length === 0) return <p className="muted">No per-criterion result was recorded.</p>
  // Numbers point into the Rubric tab's list; a list without them keeps the narrow lead column.
  const numbered = rows.some((row) => row.number != null)
  return <ul className={`criteria-list${numbered ? ' numbered' : ''}`}>{rows.map((row) => (
    <li key={row.id} className={`${row.state}${row.scored_type ? ' has-comparison' : ''}`}>
      <span className="criterion-lead">
        {numbered && <span className="criterion-number">{row.number}</span>}
        <span className={`criterion-mark ${row.state}`} aria-label={MEANING[row.state]} title={MEANING[row.state]}>
          <Icon name={row.state === 'passed' ? 'check' : row.state === 'failed' ? 'close' : row.state === 'undeclared' ? 'info' : 'minus'} size={15} />
        </span>
      </span>
      <span className="criterion-body">
        {row.number != null && onOpenCheck
          ? <button type="button" className="criterion-label criterion-label-link" onClick={() => onOpenCheck(row.id)}
                    title={`Show check ${row.number} in the rubric`}>{row.label ?? row.id}</button>
          : <span className="criterion-label">{row.label ?? row.id}</span>}
        {!row.scored_type && row.feedback && <span className="criterion-feedback">{row.feedback}</span>}
        {row.required && <span className="criterion-meta">Required to pass</span>}
        {row.state === 'skipped' && <span className="muted small">Not evaluated — the verifier stopped before this check.</span>}
        {row.state === 'undeclared' && <span className="muted small">Not declared in the published rubric map.</span>}
      </span>
      {row.scored_type && <ValueComparison row={row} />}
      {showScores && <span className="criterion-score">
        <span title="Check score">{row.state === 'skipped' || row.score == null ? '—' : row.score.toFixed(2)}</span>
        {row.weight != null && <small>Weight {Number(row.weight.toPrecision(5))}</small>}
      </span>}
    </li>
  ))}</ul>
}

/** Shared with the QC viewer so preview and production render the same results. */
export function RubricResults({ rows, onOpenCheck }: { rows: CriterionRow[]; onOpenCheck?: (checkId: string) => void }) {
  return <section className="rubric-results" aria-label="Rubric results">
    <CriteriaTable rows={rows} onOpenCheck={onOpenCheck} />
  </section>
}

/** How the checks become the number. Declared by the builder; the platform does not recompute it. */
export function RewardBasis({ spec }: { spec?: ImplementedRubricSpec | null }) {
  if (!spec?.aggregation) return null
  const { method, components, reward_summary, baseline } = spec.aggregation
  const shares = Object.entries(components ?? {})
    .map(([name, share]) => `${name} ${share}`).join(' / ')
  return <div className="reward-basis">
    <p className="small">{reward_summary}</p>
    {baseline && <p className="muted small">{baseline}</p>}
    <p className="muted small">
      <code>{method}</code>{shares && ` · ${shares}`} · declared by the builder, not verified by the platform
    </p>
  </div>
}

function ValueComparison({ row }: { row: CriterionRow }) {
  const format = (value: unknown): string => {
    if (value == null) return 'Not found'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') return Number.isInteger(value) ? String(value) : Number(value.toPrecision(5)).toString()
    return String(value)
  }
  const expected = row.expected
  let operator = '=', desired = 'Unavailable'
  switch (expected?.kind) {
    case 'equals': desired = format(expected.value); break
    case 'tolerance': operator = '≈'; desired = `${format(expected.target)} ± ${format(expected.tolerance)}`; break
    case 'range':
      operator = expected.min != null && expected.max != null ? '∈' : expected.min != null ? '≥' : '≤'
      desired = expected.min != null && expected.max != null
        ? `[${format(expected.min)}, ${format(expected.max)}]` : format(expected.min ?? expected.max)
      break
    case 'one_of': operator = '∈'; desired = `{${expected.values.map(format).join(', ')}}`; break
    case 'regex': operator = 'matches'; desired = expected.pattern; break
  }
  const actual = row.state === 'skipped' || row.actual === undefined ? 'Unavailable' : format(row.actual)
  return <span className="criterion-comparison" aria-label={`Actual ${actual}; ${operator}; desired ${desired}`}>
    <span className="criterion-value"><small>Actual</small><strong>{actual}</strong></span>
    <span className="criterion-operator">{operator}</span>
    <span className="criterion-value"><small>Desired</small><strong>{desired}</strong></span>
  </span>
}
